<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { type PlanSelectionOption, type OfferGroupDataDto, type OfferConst } from '@api/types.gen'

import TipTap from '@render/lib/TipTap'

import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { readOfferGroupForOption, readPriceCategory } from '@render/lib/planOption'
import { evalRule } from '@render/composables/useLogic'
import { useLocale } from '@render/composables/useLocale'
import { emitPayment, PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import { useTranslation } from '@render/composables/useTranslation'
import type { ColorField, SizeField } from '@render/types/styles'
import {
  composeShadow,
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import type { OfferSelectComponentFields as Model } from './definition'
import type { ConditionalBadge } from './types'

const pipe = usePipe()
const fields = defineModel<Model>('fields', {
  required: true,
  default: () => ({
    plans: { type: 'plan_selection_options', options: [] },
    valueVariable: undefined,
    selectState: 'idle',
  }),
})

const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// One boolean drives both the pricing (which offer variant is charged) and the
// styling. The author can force it on in the editor to work on the
// post-exit-intent design without firing a real exit intent — the flag is
// EDITOR-gated so it can never leak into a built pipe.
const isExitIntent = computed(
  () =>
    !!pipe.getData('var.global.hasExitIntentBeenAccepted') ||
    (isEditor.value && fields.value.previewExitIntent === true),
)

function color(f: ColorField | undefined, fallback = 'transparent') {
  return getColorStyleData(f) || fallback
}
function size(f: SizeField | undefined, fallback = '0px') {
  return getSizeStyleData(f) || fallback
}

// An exit-intent colour override falls back to its non-exit twin, which in turn
// falls back to the theme default — so the post-exit design is authored by
// setting only the tokens that actually differ.
function exitColor(
  exitField: ColorField | undefined,
  baseField: ColorField | undefined,
  fallback: string,
) {
  if (isExitIntent.value) return color(exitField, '') || color(baseField, fallback)
  return color(baseField, fallback)
}

const css = computed(() => ({
  // Container spacing
  gapRows: size(fields.value.gapRows, '16px'),
  marginTop: size(fields.value.marginTop, '16px'),
  marginBottom: size(fields.value.marginBottom, '16px'),
  maxWidth: size(fields.value.maxWidth, '480px'),
  minHeight: size(fields.value.minHeight, '92px'),

  // Option card
  paddingY: size(fields.value.paddingY, '16px'),
  paddingX: size(fields.value.paddingX, '12px'),
  gapColumns: size(fields.value.gapColumns, '4px'),
  borderRadius: size(fields.value.borderRadius, 'var(--radius-xs)'),
  borderWidth: size(fields.value.borderWidth, '1px'),
  bgIdle: color(fields.value.bgColor, 'var(--color-background-surface-raised)'),
  bgHover: color(fields.value.bgColorHover, 'var(--color-background-surface)'),
  bgPressed: color(fields.value.bgColorPressed, 'var(--color-background-surface)'),
  borderIdle: color(fields.value.borderColor, 'var(--color-input-border)'),
  borderPressed: color(fields.value.borderColorPressed, 'var(--color-input-border-active)'),
  shadowIdle: composeShadow(
    fields.value.dropShadow,
    fields.value.innerShadow,
    'var(--shadow-selectors-default)',
  ),
  shadowPressed: composeShadow(
    fields.value.pressedDropShadow,
    fields.value.pressedInnerShadow,
    'var(--shadow-selectors-default-pressed)',
  ),

  // Radio container
  bgRadioContainer: color(
    fields.value.bgColorRadioContainer,
    'var(--color-action-secondary-background)',
  ),
  bgRadioContainerPressed: color(
    fields.value.bgColorRadioContainerPressed,
    'var(--color-action-secondary-background-pressed)',
  ),
  bgRadioFill: color(fields.value.bgColorRadioFill, 'var(--color-brand-primary)'),

  // Price box
  bgPriceBox: color(fields.value.bgColorPriceBox, 'var(--color-background-subtle-accent)'),
  bgPriceBoxPressed: color(
    fields.value.bgColorPriceBoxPressed,
    'var(--color-background-subtle-accent)',
  ),

  // Highlight header
  bgHighlightHeader: color(
    fields.value.bgColorHighlightHeader,
    'var(--color-brand-primary-subtle)',
  ),
  bgHighlightHeaderPressed: color(
    fields.value.bgColorHighlightHeaderPressed,
    'var(--color-brand-primary-subtle)',
  ),

  // Text sizes
  textSizeHighlightLabel: size(fields.value.textSizeHighlightLabel, 'var(--text-body-sm)'),
  textSizeTitle: size(fields.value.textSizeTitle, 'var(--text-body-lg-size)'),
  textSizePeriodPrice: size(fields.value.textSizePeriodPrice, 'var(--text-body-lg-size)'),
  textSizePeriodLabel: size(fields.value.textSizePeriodLabel, 'var(--text-body-sm)'),
  textSizeTotalPrice: size(fields.value.textSizeTotalPrice, 'var(--text-body-sm)'),
  textSizeOldPrice: size(fields.value.textSizeOldPrice, 'var(--text-body-sm)'),
  textSizePreviousPrice: size(
    fields.value.textSizePreviousPrice,
    size(fields.value.textSizeOldPrice, 'var(--text-body-sm)'),
  ),

  // Text colors idle
  textColorHighlightLabel: color(
    fields.value.textColorHighlightLabel,
    'var(--color-action-secondary-text)',
  ),
  textColorPeriod: color(fields.value.textColorPeriod, 'var(--color-text-secondary)'),
  textColorPeriodPrice: color(fields.value.textColorPeriodPrice, 'var(--color-text-secondary)'),
  textColorPeriodLabel: color(fields.value.textColorPeriodLabel, 'var(--color-text-secondary)'),
  textColorTotalPrice: color(
    fields.value.textColorTotalPrice,
    'var(--color-action-secondary-text)',
  ),
  textColorOldPrice: exitColor(
    fields.value.exitTextColorOldPrice,
    fields.value.textColorOldPrice,
    'var(--color-text-tertiary)',
  ),
  textColorPreviousPrice: exitColor(
    fields.value.exitTextColorPreviousPrice,
    fields.value.textColorPreviousPrice,
    'var(--color-text-tertiary)',
  ),

  // Text colors pressed
  textColorHighlightLabelPressed: color(
    fields.value.textColorHighlightLabelPressed,
    'var(--color-action-secondary-text)',
  ),
  textColorPeriodPressed: color(fields.value.textColorPeriodPressed, 'var(--color-text-primary)'),
  textColorPeriodPricePressed: color(
    fields.value.textColorPeriodPricePressed,
    'var(--color-text-secondary)',
  ),
  textColorPeriodLabelPressed: color(
    fields.value.textColorPeriodLabelPressed,
    'var(--color-text-secondary)',
  ),
  textColorTotalPricePressed: color(
    fields.value.textColorTotalPricePressed,
    'var(--color-action-secondary-text)',
  ),
  textColorOldPricePressed: exitColor(
    fields.value.exitTextColorOldPrice,
    fields.value.textColorOldPricePressed,
    'var(--color-text-tertiary)',
  ),
  textColorPreviousPricePressed: exitColor(
    fields.value.exitTextColorPreviousPrice,
    fields.value.textColorPreviousPricePressed,
    'var(--color-text-tertiary)',
  ),

  // `currentColor` outside exit intent, so each line keeps matching its own text
  // and an untouched card renders byte-identical CSS.
  strikethroughColorOldPrice: isExitIntent.value
    ? color(fields.value.exitStrikethroughColorOldPrice, 'currentColor')
    : 'currentColor',
  strikethroughColorPreviousPrice: isExitIntent.value
    ? color(fields.value.exitStrikethroughColorPreviousPrice, 'currentColor')
    : 'currentColor',

  // Badge
  badgeBg: color(fields.value.badgeBgColor, 'var(--color-background-subtle)'),
  badgeTextColor: color(fields.value.badgeTextColor, 'var(--color-text-accent)'),
  badgeFontSize: size(fields.value.badgeFontSize, 'var(--text-label-sm-size)'),
  badgeFontWeight: fields.value.badgeFontWeight || 'var(--font-weight-medium)',
  badgeBorderRadius: size(fields.value.badgeBorderRadius, 'var(--radius-xs)'),
  badgeBorderWidth: size(fields.value.badgeBorderWidth, '0'),
  badgeBorderColor: color(fields.value.badgeBorderColor, 'transparent'),
  badgePadding: getSpacingStyleData(fields.value.badgePadding) || 'var(--space-2) var(--space-8)',
  badgeMargin: getSpacingStyleData(fields.value.badgeMargin) || '0',
  badgeMinHeight: size(fields.value.badgeMinHeight, '20px'),
}))

// Prevent layout micro-shifts during offers selection if we
// render the badge, as its height larger than container row. A plan with its
// own badge override reserves the space regardless of the global dropdown.
const isBadgeTurnedOn = (option: PlanSelectionOption): boolean =>
  option.badgeVisibility === 'on' ||
  (!!fields.value.badgeVisibility && fields.value.badgeVisibility !== 'off')

const previewClass = computed(() => `is-${fields.value.selectState || 'idle'}`)

const getOfferData = (offerData: OfferGroupDataDto): OfferConst => {
  if (isExitIntent.value && offerData.exit) return offerData.exit

  return offerData.regular
}

const isActiveOffer = (option: PlanSelectionOption): boolean => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return false

  const selectedOffer = selectedOfferData.value
  if (!selectedOffer || Array.isArray(selectedOffer)) return false

  const selectedVariant = getOfferData(selectedOffer)
  const optionVariant = getOfferData(offerData)
  return (
    selectedVariant?.apiBasePrice === optionVariant?.apiBasePrice &&
    selectedVariant?.ppoId === optionVariant?.ppoId
  )
}

const isBestDealOffer = (option: PlanSelectionOption): boolean => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return false

  const variant = getOfferData(offerData)
  const periodPrice = isTrialPrice(variant)
    ? variant.trialPricePerPeriod
    : variant.basePricePerPeriod
  if (!periodPrice) return false

  // Gathered all period prices to compare agaust current periodPrice and determine if it's the best deal
  const allPeriodPrices = (fields.value.plans?.options ?? [])
    .map((opt) => {
      const data = getOfferDataForOption(opt)
      if (!data) return null

      const offerData = getOfferData(data)
      return isTrialPrice(offerData) ? offerData.trialPricePerPeriod : offerData.basePricePerPeriod
    })
    .filter((price): price is number => price !== undefined)

  return allPeriodPrices.every((price) => periodPrice <= price)
}

const priceCategory = computed(() =>
  readPriceCategory((path) => pipe.getData(path), fields.value.priceCategory),
)

const getOfferDataForOption = (option: PlanSelectionOption): OfferGroupDataDto | undefined =>
  readOfferGroupForOption((path) => pipe.getData(path), option, priceCategory.value)

const isDefaultOption = (option: PlanSelectionOption): boolean =>
  !!fields.value.plans?.defaultOptionId && option.id === fields.value.plans.defaultOptionId

const clickOption = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)

  if (!offerData) {
    console.warn(
      `No offer group data for plan option ${option.id} (category key ${option.categoryKey ?? 'none'}, constant ${option.offerGroup?.key ?? 'none'}). Cannot set current offer group.`,
    )
    return
  }

  selectedOfferData.value = offerData
  pipe.setData('var.global.selectedOfferGroupIsDefault', isDefaultOption(option))
  onClick()
}

// HELPERS: Start
const isTrialPrice = (offer: OfferConst): boolean => {
  if (offer.paymentType !== 'recurring_with_trial') return false

  return true
}

const getPeriodUnitText = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return ''

  const variant = getOfferData(offerData)
  if (isTrialPrice(variant)) return variant.trialPeriodUnit

  return variant.basePeriodUnit
}

const locale = useLocale()
const formatPrice = (value: number | string | undefined) => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') value = parseFloat(value)
  return locale.formatCurrencyShort(value)
}

const getPeriodPrice = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return ''

  const variant = getOfferData(offerData)
  if (isTrialPrice(variant)) return formatPrice(variant.trialPricePerPeriod)

  return formatPrice(variant.basePricePerPeriod)
}

const getOldPrice = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return ''

  const variant = getOfferData(offerData)
  if (isTrialPrice(variant)) return formatPrice(variant.trialPriceWoDiscount)

  return formatPrice(variant.basePriceWoDiscount)
}

const getPrice = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return ''

  const variant = getOfferData(offerData)
  if (isTrialPrice(variant)) return formatPrice(variant.apiTrialPrice)

  return formatPrice(variant.apiBasePrice)
}

// The pre-exit-intent price of the same plan, which the post-exit design strikes
// through beside the original — read off `regular` regardless of which variant
// is charged.
const getPreviousPrice = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return ''

  const regular = offerData.regular
  if (isTrialPrice(regular)) return formatPrice(regular.apiTrialPrice)

  return formatPrice(regular.apiBasePrice)
}

// Only meaningful once the exit offer is actually in play. An offer group with no
// `exit` variant is still charged its regular price, so `previous` would repeat
// the charged figure; and a regular price with no discount already equals the old
// price, so it would be struck through twice.
const showPreviousPrice = (option: PlanSelectionOption): boolean => {
  if (!isExitIntent.value || fields.value.exitShowPreviousPrice !== true) return false

  const offerData = getOfferDataForOption(option)
  if (!offerData?.exit) return false

  const previous = getPreviousPrice(option)
  return !!previous && previous !== getOldPrice(option)
}

const isOfferOptionPeriodUnitBased = (
  option: PlanSelectionOption,
  periodOption: string,
): boolean => {
  const offerData = getOfferDataForOption(option)
  if (!offerData || !offerData.regular) return false

  const periodUnit = getPeriodUnitText(option)
  return periodUnit === periodOption
}
// HELPERS: End

const selectedOfferData = computed<OfferGroupDataDto>({
  get() {
    return (pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`) ?? []) as OfferGroupDataDto
  },
  set(val) {
    pipe.setData(`var.${fields.value.valueVariable?.key ?? ''}`, val)
  },
})

// True when the stored selection corresponds to one of THIS selector's
// options. Drives "seed the default only when nothing valid is selected": a
// user pick (which matches an option) is never clobbered, while a cross-pipe
// restored offer (which matches nothing here) falls back to the default.
const hasValidSelection = computed(() => (fields.value.plans?.options ?? []).some(isActiveOffer))

function applyDefaultOption(defaultId: string | undefined) {
  if (!defaultId) return
  // Never overwrite a selection that already matches one of our options.
  if (hasValidSelection.value) return

  const defaultOption = fields.value.plans?.options.find((o) => o.id === defaultId)
  if (!defaultOption) return

  const offerData = getOfferDataForOption(defaultOption)
  if (!offerData) return

  selectedOfferData.value = offerData
  pipe.setData('var.global.selectedOfferGroupIsDefault', true)
  // Mirrors legacy `initDefaultSelectedPlan` — fires `plan selected` for the
  // default-selected offer too, not only on user click.
  emitPayment({ pipe, event: PAYMENT_EVENTS.planSelected, offer: offerData })
  emitPayment({ pipe, event: PAYMENT_EVENTS.trackingPlanSelectedSlimPayload, offer: offerData }) // Smaller payload, meant for Klaviyo/Segment so that payload matches to the rest of their payload and CPPv3
}

onMounted(async () => {
  // The persisted-vars merge runs inside a setTimeout(0); wait for it so we read
  // the real selection (and any AC-restored offer) before deciding to seed.
  await pipe.whenReady()
  applyDefaultOption(fields.value.plans?.defaultOptionId)
})

// Re-evaluate when the stored offer changes (e.g. an AC restore lands after
// mount and writes a non-matching offer) or the configured default changes.
// Self-settling: applying the default writes a value that matches an option, so
// `hasValidSelection` flips true and the next run no-ops.
watch(
  () => [selectedOfferData.value, fields.value.plans?.defaultOptionId],
  () => applyDefaultOption(fields.value.plans?.defaultOptionId),
)

const bestDealValue = useTranslation(() => fields.value.bestDealTranslation?.key)
const periodTranslationForMinute = useTranslation(
  () => fields.value.periodTranslationForMinute?.key,
)
const periodTranslationForDay = useTranslation(() => fields.value.periodTranslationForDay?.key)
const periodTranslationForMonth = useTranslation(() => fields.value.periodTranslationForMonth?.key)

const conditionalBadges = computed(() => fields.value.conditionalBadges?.items ?? [])

// Collect every `{ field: '...' }` key referenced in a json-logic rule tree so we
// know which pipe-data paths to re-evaluate on. Mirrors NutritionistChat.
function collectRuleFieldKeys(rule: unknown, out: Set<string>) {
  if (!rule || typeof rule !== 'object') return
  const node = rule as Record<string, unknown>
  if ('field' in node) {
    const arg = Array.isArray(node.field) ? (node.field as unknown[])[0] : node.field
    if (typeof arg === 'string') out.add(arg)
  }
  for (const child of Object.values(node)) {
    if (Array.isArray(child)) child.forEach((c) => collectRuleFieldKeys(c, out))
    else if (child && typeof child === 'object') collectRuleFieldKeys(child, out)
  }
}

// Plan options that opted into their own badge label list, keyed by option id —
// these override the shared conditionalBadges list below for that option only.
const optionOverrides = computed(() =>
  (fields.value.plans?.options ?? []).filter((o) => o.badgeVisibility === 'on'),
)

const ruleDependencyKeys = computed(() => {
  const keys = new Set<string>()
  for (const item of conditionalBadges.value) collectRuleFieldKeys(item.rules, keys)
  for (const option of optionOverrides.value) {
    for (const item of option.conditionalBadges?.items ?? []) collectRuleFieldKeys(item.rules, keys)
  }
  return Array.from(keys)
})

// Resolve a badge list (shared or a plan option's own) to the translation key of
// its first matching item, or undefined when none match. `evalRule` is
// fail-open, so an item with no rule always matches — that's how an
// unconditional badge is expressed in the list.
async function resolveActiveBadgeKey(items: ConditionalBadge[]): Promise<string | undefined> {
  // In the editor, rules are evaluated so the canvas reflects the current
  // conditions - unless the author turned visibility rules off ("show all"),
  // in which case fall back to the first authored text so styling stays
  // visible. Live builds always evaluate.
  if (isEditor.value && pipe.disableAllVisibilityRules.value) {
    return items.find((i) => i.text?.key)?.text?.key
  }
  for (const item of items) {
    if (!item.text?.key) continue
    if (await evalRule(item.rules, (k) => pipe.getData(k))) return item.text.key
  }
  return undefined
}

// Translation key of the active shared badge. Re-evaluated whenever the list or
// any referenced pipe-data value changes.
const activeBadgeKey = ref<string | undefined>(undefined)
// Same, but per plan option that opted into its own list (keyed by option id).
const optionActiveBadgeKeys = ref<Record<string, string | undefined>>({})

watch(
  [
    isEditor,
    () => pipe.disableAllVisibilityRules.value,
    conditionalBadges,
    optionOverrides,
    () => ruleDependencyKeys.value.map((k) => pipe.getData(k)),
  ],
  async () => {
    activeBadgeKey.value = await resolveActiveBadgeKey(conditionalBadges.value)

    const next: Record<string, string | undefined> = {}
    for (const option of optionOverrides.value) {
      next[option.id] = await resolveActiveBadgeKey(option.conditionalBadges?.items ?? [])
    }
    optionActiveBadgeKeys.value = next
  },
  { immediate: true, deep: true },
)

// Resolved badge text for the shared list. The list is the source of truth;
// only when it's empty do we fall back to the legacy single `badgeTranslation`
// so pre-list pipes render.
const resolvedBadgeKey = computed(() =>
  conditionalBadges.value.length > 0 ? activeBadgeKey.value : fields.value.badgeTranslation?.key,
)

// A plan option with its own badge list (badgeVisibility: 'on') overrides the
// shared/legacy badge text — for that option only.
const badgeKeyForOption = (option: PlanSelectionOption): string | undefined =>
  option.badgeVisibility === 'on' ? optionActiveBadgeKeys.value[option.id] : resolvedBadgeKey.value

function setBadgeTranslation(option: PlanSelectionOption, value: object) {
  const key = badgeKeyForOption(option)
  if (key) pipe.setTranslation(key, value)
}

const shouldShowBadge = (option: PlanSelectionOption): boolean => {
  if (!badgeKeyForOption(option)) return false

  // A plan with its own badge override shows it unconditionally — the override
  // replaces the global "which cards show a badge" gate for that option, not
  // just its text. The global dropdown still gates every option with no
  // override of its own.
  if (option.badgeVisibility === 'on') return true

  switch (fields.value.badgeVisibility) {
    case 'all':
      return true
    case 'selected':
      return isActiveOffer(option)
    case 'best-deal':
      return isBestDealOffer(option)
    default:
      return false
  }
}

const emit = defineEmits<{
  (e: 'onClick'): void
}>()

function onClick() {
  emit('onClick')
}
</script>

<template>
  <!-- Single-select offer picker: the options are radios, not plain buttons, so
       the selected one is announced as such. Mirrors OfferSelectV2Card. -->
  <div class="offer-select-container" role="radiogroup">
    <button
      v-for="option in fields.plans?.options ?? []"
      :key="option.id"
      :data-part="option.id"
      @click="clickOption(option)"
      type="button"
      role="radio"
      :aria-checked="isActiveOffer(option)"
      class="normalized-button offer-select-option"
      :class="[{ active: isActiveOffer(option) }, previewClass]"
    >
      <div class="offer-select-best-deal" v-if="isBestDealOffer(option)">
        <span class="offer-select-tiptap-line">
          <TipTap v-model="bestDealValue" mode="single" />
        </span>
      </div>
      <div class="offer-select-content">
        <!-- Checkmark(radio-button type) -->
        <div class="offer-select-check">
          <div class="offer-select-check-inactive" v-if="!isActiveOffer(option)"></div>
          <div class="offer-select-check-active" v-if="isActiveOffer(option)">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="12" fill="#E9F6FC" />
              <circle cx="12" cy="12" r="6" fill="#29ABE2" />
            </svg>
          </div>
        </div>

        <!-- Information about current offer pricing -->
        <div class="offer-select-info">
          <div class="offer-select-title">
            <TipTap
              :model-value="pipe.getTranslation(option.label.key)"
              @update:model-value="(val) => pipe.setTranslation(option.label.key, val as object)"
              mode="single"
            />
          </div>

          <div class="offer-select-info-pricing">
            <span class="offer-select-old-price" v-text="getOldPrice(option)"></span>
            <!-- Struck on its own line, not joined to the old price above. -->
            <span
              class="offer-select-previous-price"
              v-if="showPreviousPrice(option)"
              v-text="getPreviousPrice(option)"
            ></span>
            <span class="offer-select-total-price" v-text="getPrice(option)"></span>
            <span
              v-if="isBadgeTurnedOn(option)"
              class="offer-select-badge"
              :class="{ 'offer-select-badge--hidden': !shouldShowBadge(option) }"
              :aria-hidden="!shouldShowBadge(option)"
            >
              <span class="offer-select-tiptap-line">
                <TipTap
                  :model-value="pipe.getTranslation(badgeKeyForOption(option))"
                  @update:model-value="(val) => setBadgeTranslation(option, val as object)"
                  mode="single"
                />
              </span>
            </span>
          </div>
        </div>

        <!-- Information about period pricing -->
        <div class="offer-select-price-info">
          <div class="offer-select-period-price" v-text="getPeriodPrice(option)"></div>
          <div class="offer-select-period-unit-wrapper">
            <span class="offer-select-tiptap-line">
              <TipTap
                v-if="
                  fields.periodTranslationForMinute?.key &&
                  isOfferOptionPeriodUnitBased(option, 'minute')
                "
                v-model="periodTranslationForMinute"
                mode="single"
              />
              <TipTap
                v-if="
                  fields.periodTranslationForDay?.key && isOfferOptionPeriodUnitBased(option, 'day')
                "
                v-model="periodTranslationForDay"
                mode="single"
              />
              <TipTap
                v-if="
                  fields.periodTranslationForMonth?.key &&
                  isOfferOptionPeriodUnitBased(option, 'month')
                "
                v-model="periodTranslationForMonth"
                mode="single"
              />
            </span>
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<style scoped lang="scss">
.normalized-button {
  all: unset;
  display: inline-block;
  text-align: center;
  border: none;
}

.offer-select-container {
  display: flex;
  width: 100%;
  max-width: v-bind('css.maxWidth');
  min-height: v-bind('css.minHeight');
  flex-direction: column;
  gap: v-bind('css.gapRows');
  margin-top: v-bind('css.marginTop');
  margin-bottom: v-bind('css.marginBottom');
  cursor: pointer;
}

.offer-select-best-deal {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2px 64px;
  background: v-bind('css.bgHighlightHeader');
  color: v-bind('css.textColorHighlightLabel');
  font-size: v-bind('css.textSizeHighlightLabel');
  font-weight: 900;
  line-height: 22px;
  text-transform: uppercase;
}

.offer-select-option {
  display: block;
  position: relative;
  text-align: start;
  overflow: hidden;
  border-style: solid;
  border-radius: v-bind('css.borderRadius');
  border-width: v-bind('css.borderWidth');
  background-color: v-bind('css.bgIdle');
  border-color: v-bind('css.borderIdle');
  box-shadow: v-bind('css.shadowIdle');

  // `.is-hover` is the editor's author-preview state class and stays unconditional. Gate
  // the real `:hover` pseudo behind a hover-capable pointer so a tapped offer doesn't stay
  // highlighted after a tap-driven page navigation (touch emulates and sticks `:hover`).
  &.is-hover {
    background-color: v-bind('css.bgHover');
  }

  @media (hover: hover) {
    &:hover {
      background-color: v-bind('css.bgHover');
    }
  }

  // `all: unset` on `.normalized-button` drops the UA focus ring, so keyboard
  // focus reuses the hover background rather than introducing an outline of its
  // own. Unconditional: `:focus-visible` never matches a tap, so it does not
  // want the hover-capability gate above.
  &:focus-visible {
    outline: none;
    background-color: v-bind('css.bgHover');
  }

  &.active,
  &.is-pressed {
    background-color: v-bind('css.bgPressed');
    border-color: v-bind('css.borderPressed');
    box-shadow: v-bind('css.shadowPressed');

    .offer-select-best-deal {
      background: v-bind('css.bgHighlightHeaderPressed');
      color: v-bind('css.textColorHighlightLabelPressed');
    }

    .offer-select-check-inactive,
    .offer-select-check-active circle:first-child {
      background-color: v-bind('css.bgRadioContainerPressed');
      fill: v-bind('css.bgRadioContainerPressed');
    }

    .offer-select-check-active circle:last-child {
      fill: v-bind('css.bgRadioFill');
    }

    .offer-select-period-price {
      background-color: v-bind('css.bgPriceBoxPressed');
      color: v-bind('css.textColorPeriodPricePressed');
    }

    .offer-select-title {
      color: v-bind('css.textColorPeriodPressed');
    }

    .offer-select-old-price {
      color: v-bind('css.textColorOldPricePressed');
    }

    .offer-select-previous-price {
      color: v-bind('css.textColorPreviousPricePressed');
    }

    .offer-select-total-price {
      color: v-bind('css.textColorTotalPricePressed');
    }

    .offer-select-period-unit-wrapper {
      color: v-bind('css.textColorPeriodLabelPressed');
    }
  }
}

.offer-select-content {
  display: flex;
  align-items: center;
  padding: v-bind('css.paddingY') v-bind('css.paddingX');
  gap: v-bind('css.gapColumns');
}

.offer-select-check {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}

.offer-select-check-inactive {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: v-bind('css.bgRadioContainer');
}

.offer-select-check-active {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  circle:first-child {
    fill: v-bind('css.bgRadioContainer');
  }
  circle:last-child {
    fill: v-bind('css.bgRadioFill');
  }
}

.offer-select-info {
  padding-block: 0;
  padding-inline: 16px 12px;
  flex: 1 1 100%;
}

.offer-select-price-info {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  text-align: center;
}

.offer-select-title {
  font-size: v-bind('css.textSizeTitle');
  font-weight: 900;
  line-height: 32px;
  color: v-bind('css.textColorPeriod');
}

.offer-select-info-pricing {
  --offer-select-pricing-gap: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-weight: 500;
  gap: var(--offer-select-pricing-gap);
}

// Each price is struck separately — two lines with the row gap clear between
// them, not one rule spanning the pair. `text-decoration-color` is what lets a
// grey price carry a red line after exit intent; it resolves to `currentColor`
// outside exit intent, which is the decoration's own default.
.offer-select-old-price {
  text-decoration: line-through;
  text-decoration-color: v-bind('css.strikethroughColorOldPrice');
  font-size: v-bind('css.textSizeOldPrice');
  color: v-bind('css.textColorOldPrice');
  line-height: 22px;
}

.offer-select-previous-price {
  text-decoration: line-through;
  text-decoration-color: v-bind('css.strikethroughColorPreviousPrice');
  font-size: v-bind('css.textSizePreviousPrice');
  color: v-bind('css.textColorPreviousPrice');
  line-height: 22px;
}

.offer-select-total-price {
  font-size: v-bind('css.textSizeTotalPrice');
  font-weight: 900;
  line-height: 22px;
  color: v-bind('css.textColorTotalPrice');
}

.offer-select-period-price {
  border-radius: 8px;
  padding: 4px 8px;
  white-space: nowrap;
  background-color: v-bind('css.bgPriceBox');
  font-size: v-bind('css.textSizePeriodPrice');
  font-weight: 900;
  line-height: 32px;
  color: v-bind('css.textColorPeriodPrice');
}

.offer-select-tiptap-line {
  min-width: 0;
}

.offer-select-period-unit-wrapper {
  display: flex;
  font-size: v-bind('css.textSizePeriodLabel');
  font-weight: 500;
  line-height: 22px;
  color: v-bind('css.textColorPeriodLabel');
}

.offer-select-period-unit-text {
  margin: 0;
}

.offer-select-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  background-color: v-bind('css.badgeBg');
  color: v-bind('css.badgeTextColor');
  font-family: var(--text-label-sm-family);
  font-size: v-bind('css.badgeFontSize');
  font-weight: v-bind('css.badgeFontWeight');
  line-height: var(--text-label-sm-line-height);
  border-radius: v-bind('css.badgeBorderRadius');
  border: v-bind('css.badgeBorderWidth') solid v-bind('css.badgeBorderColor');
  padding: v-bind('css.badgePadding');
  margin: v-bind('css.badgeMargin');
  min-height: v-bind('css.badgeMinHeight');

  &--hidden {
    visibility: hidden;
    pointer-events: none;
    width: 0;
    padding-inline: 0;
    border-inline-width: 0;
    margin-inline: calc(-1 * var(--offer-select-pricing-gap)) 0;
    overflow: hidden;

    .offer-select-tiptap-line {
      flex-shrink: 0;
      width: max-content;
    }
  }
}
</style>
